import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchInput from 'components/SearchInput/SearchInput';
import Pagination from 'components/Pagination/Pagination';
import AddUncataloguedCardModal, {
  AddUncataloguedSelection,
} from 'components/EditHaveListCardModal/AddUncataloguedCardModal';
import { AssetUrls } from 'config/assets';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useFetchPublicWantListQuery } from 'store/services/collection/publicCollectionApi';
import { useAddCardToHaveListMutation } from 'store/services/collectionList/haveList/haveListApi';
import {
  useGetReceiveCandidatesQuery,
  useGetSendCandidatesQuery,
} from 'store/services/tradeOffers/tradeOffersApi';
import { CollectionList } from 'types/interfaces/collectionList';
import {
  OfferCandidateRow,
  OfferCandidateWantGroup,
  OfferLineItem,
} from 'types/interfaces/tradeOffer';
import {
  allowedConditionsFor,
  defaultDeclaredCondition,
  priceForCondition,
} from 'utils/offerConditions';
import { formatConditionShort, parseOfferError } from 'utils/offerErrors';
import { useOfferBuilder, OfferSide } from '../context/OfferBuilderContext';
import { useSendMatchIndex } from '../hooks/useSendMatchIndex';
import { useWantListPriceFill } from '../hooks/useWantListPriceFill';
import OfferCardRow from './OfferCardRow';
import WantListInfoRow from './WantListInfoRow';

const PANEL_PAGE_SIZE = 25;

/**
 * The want record a send-side row satisfies. Passed as one object rather than
 * loose ids so the binding and the budget can never drift apart at a call site.
 * Undefined on the receive side, which has no want context at all.
 */
type WantContext = OfferCandidateWantGroup['wantEntry'];

/**
 * Send-side rows carry the recipient's accepted conditions and the
 * per-condition quote map; the line defaults to the row's own condition when
 * accepted (untouched selector = today's payload), else the closest accepted
 * declaration, priced from the map. Receive-side rows pass no want context.
 *
 * The want binding travels onto the line here — not just for un-catalogued
 * adds — because it is what makes three printings of one wanted card share a
 * single allowance instead of each carrying its own.
 */
const rowToLineItem = (row: OfferCandidateRow, wantEntry?: WantContext): OfferLineItem => {
  const acceptedConditions = wantEntry?.acceptedConditions;
  const allowed = allowedConditionsFor(row.condition, acceptedConditions);
  const declared = defaultDeclaredCondition(row.condition, allowed) ?? row.condition;
  return {
    userMTGCardHaveId: row.userMTGCardHaveId,
    quantity: 1,
    ownedQuantity: row.ownedQuantity,
    // Unaddable rows never reach here, so a priced condition always exists
    unitPricePoints: (priceForCondition(row, declared) ?? row.quotedPricePoints) as number,
    foil: row.foil,
    condition: row.condition,
    declaredCondition: declared,
    quotedPricesByCondition: row.quotedPricesByCondition,
    rowQuotedPricePoints: row.quotedPricePoints,
    acceptedConditions,
    ...(wantEntry
      ? { originatingWantEntryId: wantEntry.id, wantEntryQuantity: wantEntry.quantity }
      : {}),
    card: row.card,
  };
};

interface SourcePanelProps {
  /** 'send' = their want list (fills You send); 'receive' = their have list (fills They send) */
  side: 'send' | 'receive';
  /** Right panel only: whether the target's full have list is public */
  fullHaveListPublic?: boolean;
  /**
   * Raised when the candidates fetch is rejected with PROPOSER_OFFERS_DISABLED
   * — the VIEWER has trade offers turned off, so the builder cannot even load.
   * The page owns the remedy (the enable-in-place dialog); this panel only
   * reports it, and suppresses its own error copy so the dialog is the single
   * thing on screen.
   */
  onProposerOffersDisabled?: () => void;
}

/**
 * One source panel, two instances. Opens on the intersection (never the raw
 * list — a real profile runs to 5,010 have rows) with a visible escape hatch
 * to the full list. Search and pagination are server-side: filter first,
 * paginate second.
 */
const SourcePanel: React.FC<SourcePanelProps> = ({
  side,
  fullHaveListPublic = true,
  onProposerOffersDisabled,
}) => {
  const {
    counterpartyId,
    counterpartyUsername,
    addItem,
    getAddedQuantity,
    getWantEntryAddedQuantity,
    markRowCreatedByBuilder,
    youSendAtCap,
    theySendAtCap,
  } = useOfferBuilder();

  const targetSide: OfferSide = side === 'send' ? 'youSend' : 'theySend';
  const atCap = side === 'send' ? youSendAtCap : theySendAtCap;

  const [mode, setMode] = useState<'intersection' | 'full'>('intersection');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  // Un-catalogued add: the want entry whose "which copy do you own?" control
  // is open. Which entries are already added is NOT tracked here — it is derived
  // from the package below, so removing the line reverts the button.
  const [addingWant, setAddingWant] = useState<CollectionList | null>(null);
  const [addCardToHaveList] = useAddCardToHaveListMutation();

  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  // Clearing the field bypasses the debounce so one click restores the list
  const effectiveSearch = searchTerm === '' ? '' : debouncedSearch;

  useEffect(() => {
    setPage(1);
  }, [effectiveSearch, mode]);

  // Full mode differs by side: offer-candidates implements mode=full for
  // side=receive only. The send panel's full mode is the counterparty's
  // PUBLIC WANT LIST (informational; no privacy gate exists for want lists),
  // with viewer ownership joined in client-side from the intersection.
  const isFullWantList = side === 'send' && mode === 'full';

  // sort=value_desc on the DISPLAY queries only. The count probes below stay
  // unsorted: they read totalCount alone, and a differing arg would just spawn
  // a second cache entry for the same number.
  const sendQuery = useGetSendCandidatesQuery(
    {
      userId: counterpartyId,
      mode: 'intersection',
      page,
      limit: PANEL_PAGE_SIZE,
      search: effectiveSearch.trim() || undefined,
      sort: 'value_desc',
    },
    { skip: side !== 'send' || isFullWantList }
  );
  const receiveQuery = useGetReceiveCandidatesQuery(
    {
      userId: counterpartyId,
      mode,
      page,
      limit: PANEL_PAGE_SIZE,
      search: effectiveSearch.trim() || undefined,
      sort: 'value_desc',
    },
    { skip: side !== 'receive' }
  );
  const wantListQuery = useFetchPublicWantListQuery(
    {
      username: counterpartyUsername,
      page,
      limit: PANEL_PAGE_SIZE,
      search: effectiveSearch.trim() || undefined,
    },
    { skip: !isFullWantList }
  );

  // Ownership index over the whole intersection: which want entries the
  // viewer can actually add, and with which have rows (decisions 2-4).
  const matchIndex = useSendMatchIndex(counterpartyId, isFullWantList);

  // Tiny limit-1 count queries: the subtitle always shows the match count and
  // the full-list toggle is labeled with its real total (decision 15). The
  // send side's full total is the public want list's totalRecords (unique
  // entries, matching rendered rows), not an offer-candidates count.
  const countArgs = { userId: counterpartyId, page: 1, limit: 1 };
  const sendMatchCount = useGetSendCandidatesQuery(
    { ...countArgs, mode: 'intersection' as const },
    { skip: side !== 'send' }
  );
  const receiveMatchCount = useGetReceiveCandidatesQuery(
    { ...countArgs, mode: 'intersection' as const },
    { skip: side !== 'receive' }
  );
  const wantListTotalQuery = useFetchPublicWantListQuery(
    { username: counterpartyUsername, page: 1, limit: 1 },
    { skip: side !== 'send' }
  );
  const receiveFullCount = useGetReceiveCandidatesQuery(
    { ...countArgs, mode: 'full' as const },
    { skip: side !== 'receive' || !fullHaveListPublic }
  );
  const matchCount = (side === 'send' ? sendMatchCount : receiveMatchCount).data?.totalCount;
  const fullListTotal =
    side === 'send'
      ? wantListTotalQuery.data?.totalRecords
      : receiveFullCount.data?.totalCount;

  // Batched display prices for unowned want rows without a displayPrice.
  // Informational only — addable rows always price from quotedPricePoints.
  const unownedRows = useMemo(
    () =>
      isFullWantList && matchIndex.isReady && wantListQuery.data
        ? wantListQuery.data.wantList.filter((entry) => !matchIndex.index.has(entry.id))
        : undefined,
    [isFullWantList, matchIndex.isReady, matchIndex.index, wantListQuery.data]
  );
  const priceMap = useWantListPriceFill(unownedRows);

  const isLoading =
    side === 'receive'
      ? receiveQuery.isLoading
      : isFullWantList
        ? wantListQuery.isLoading || matchIndex.isLoading
        : sendQuery.isLoading;
  const isFetching =
    side === 'receive'
      ? receiveQuery.isFetching
      : isFullWantList
        ? wantListQuery.isFetching || matchIndex.isLoading
        : sendQuery.isFetching;
  const error =
    side === 'receive' ? receiveQuery.error : isFullWantList ? wantListQuery.error : sendQuery.error;
  const hasData =
    side === 'receive'
      ? !!receiveQuery.data
      : isFullWantList
        ? !!wantListQuery.data
        : !!sendQuery.data;

  // Full-want-mode displayed count: the public endpoint returns no
  // filteredCount, so during a search derive the filtered total from the page
  // in hand (exact once hasMore is false) — the displayed count must always
  // agree with rendered rows, and pagination must not offer empty pages.
  const wantData = wantListQuery.data;
  const fullWantCount =
    wantData == null
      ? 0
      : wantData.filteredCount ??
        (effectiveSearch.trim()
          ? wantData.hasMore
            ? wantData.totalRecords
            : (page - 1) * PANEL_PAGE_SIZE + wantData.wantList.length
          : wantData.totalRecords);

  // If the result set shrinks under us, fall back to the last valid page
  const totalCount =
    side === 'receive'
      ? receiveQuery.data?.totalCount ?? 0
      : isFullWantList
        ? fullWantCount
        : sendQuery.data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PANEL_PAGE_SIZE));
  useEffect(() => {
    if (hasData && page > 1 && page > totalPages) {
      setPage(totalPages);
    }
  }, [hasData, page, totalPages]);

  const parsedError = useMemo(() => (error ? parseOfferError(error) : null), [error]);
  // Full-list mode on a PRIVATE HAVE LIST is a normal state, not an error.
  // Receive-only by construction: want lists carry no privacy flag, so the
  // gate must never leak into the send panel (decision 6).
  const isPrivateHaveList =
    side === 'receive' && parsedError?.kind === 'private_have_list';

  // The viewer's OWN setting blocks the fetch (reciprocity). Hand it to the page
  // so the enable-in-place dialog can offer the fix and then refetch; rendering
  // an error here as well would put a dead-end message behind the dialog.
  const isProposerOffersDisabled = parsedError?.kind === 'proposer_offers_disabled';
  useEffect(() => {
    if (isProposerOffersDisabled) onProposerOffersDisabled?.();
  }, [isProposerOffersDisabled, onProposerOffersDisabled]);

  /**
   * Un-catalogued add. Creates the have row the offer line needs, then seeds
   * the line from that row.
   *
   * `forTrade: false` is mandatory and must be explicit: the backend defaults
   * it to `true` when omitted, which would publish the sender's card to every
   * forTrade-gated surface. Adding one card to one offer must not advertise it
   * to the whole site.
   *
   * The created row is not in the candidates cache yet — invalidation is in
   * flight — so the line is built from the add response rather than waiting
   * for a refetch. Its price comes from the row's own backend-computed
   * `displayPrice`; nothing here estimates a price.
   */
  const handleUncataloguedAdd = useCallback(
    async (want: CollectionList, selection: AddUncataloguedSelection) => {
      const row = await addCardToHaveList({
        cardId: selection.cardId,
        quantity: 1,
        condition: selection.condition,
        foil: selection.foil,
        language: selection.language,
        forTrade: false,
        source: 'offer-builder-want-list',
      }).unwrap();

      const price = row.displayPrice;
      if (price == null) {
        // The row exists but cannot be priced, so it cannot be part of an
        // offer. Say so rather than adding an unpriceable line the backend
        // would reject at send time with OFFER_UNPRICEABLE_ITEM.
        throw new Error(
          'This card was added to your have list, but it has no price data and cannot be included in an offer.'
        );
      }

      // Built from the SELECTED printing, not the want entry's default card:
      // for an any-set want those are usually different printings, and the
      // line must show the copy the sender actually said they own. Assembled
      // field by field rather than cast — MTGCard carries neither `scryfallId`
      // nor `imageUrl`, so a cast would silently render an empty image.
      const { printing } = selection;
      const card: OfferLineItem['card'] = {
        id: printing.id,
        oracleId: want.oracleId ?? want.card?.oracleId ?? null,
        scryfallId: printing.scryfallID || null,
        name: want.cardName || want.card?.name || '',
        setCode: printing.setCode,
        setName: printing.setName,
        collectorNumber: printing.collectorNumber,
        lang: printing.lang,
        imageUrl: printing.scryfallID
          ? AssetUrls.cards.getCardImage(printing.scryfallID, 'normal')
          : null,
      };

      addItem(targetSide, {
        userMTGCardHaveId: row.id,
        quantity: 1,
        ownedQuantity: row.quantity,
        unitPricePoints: price,
        foil: row.foil,
        condition: row.condition,
        declaredCondition: row.condition,
        rowQuotedPricePoints: price,
        acceptedConditions: want.acceptableConditions,
        // Binds the line back to the want row that spawned it, which is what
        // lets that row show "Added" without a second copy of the state, and
        // charges it against that want's allowance like any other line.
        originatingWantEntryId: want.id,
        wantEntryQuantity: want.quantity,
        card,
      });

      // Pass the server's post-add quantity, not a boolean: the context owns
      // the create-vs-increment rule so the "never delete a pre-existing row"
      // guarantee lives in exactly one place.
      markRowCreatedByBuilder(row.id, row.quantity, 1);
    },
    [addCardToHaveList, addItem, targetSide, markRowCreatedByBuilder]
  );

  const renderAddAction = (row: OfferCandidateRow, wantEntry?: WantContext) => {
    const acceptedConditions = wantEntry?.acceptedConditions;
    const added = getAddedQuantity(targetSide, row.userMTGCardHaveId) > 0;
    // Send side: a row is addable only if at least one condition the recipient
    // accepts has price data (add-time blocking, not propose-time — the whole
    // point of the condition ticket). Receive side keeps the plain price gate.
    const allowed =
      side === 'send' ? allowedConditionsFor(row.condition, acceptedConditions) : null;
    const conditionBlocked = allowed != null && allowed.length === 0;
    const noPrice =
      allowed != null
        ? !conditionBlocked && !allowed.some((c) => priceForCondition(row, c) != null)
        : row.quotedPricePoints == null;

    // The want budget is shared by every printing under this group, so a
    // sibling row can be spent-out without this row itself being added. That
    // is the whole bug: gating on `added` alone let 3 copies of a card they
    // want 1 of into the offer, one printing at a time.
    const spent = wantEntry ? getWantEntryAddedQuantity(targetSide, wantEntry.id) : 0;
    const wantExhausted = wantEntry != null && !added && spent >= wantEntry.quantity;

    const disabled = added || wantExhausted || noPrice || conditionBlocked || atCap;
    // "Added" for the spent-out siblings too — the recipient's allowance is
    // filled, which is what the label is really reporting.
    const label = added || wantExhausted ? 'Added' : 'Add';
    const reason = noPrice
      ? 'No price data for this card, so it can’t be added to an offer'
      : wantExhausted && wantEntry
        ? // Without this a disabled "Added" on a row they never clicked reads
          // as a bug rather than as the rule doing its job.
          `They only want ${wantEntry.quantity}, and you've already added ${spent}. Remove one to swap printings.`
        : atCap && !added
          ? 'This side is at the 100-card limit'
          : undefined;
    return (
      <div className="offer-add-action">
        <button
          type="button"
          className="offer-add-btn"
          disabled={disabled}
          title={reason}
          onClick={() => addItem(targetSide, rowToLineItem(row, wantEntry))}
        >
          {label}
        </button>
        {conditionBlocked && acceptedConditions && (
          // Visible, not a tooltip: the reason names the accepted conditions
          <div className="offer-add-reason">
            They accept {acceptedConditions.map(formatConditionShort).join(', ')} only
          </div>
        )}
      </div>
    );
  };

  /**
   * "· they want 2 · 1 left" — the budget made visible before the user hits
   * it, mirroring the ProfileWantListTab treatment (`{remaining} left`) rather
   * than inventing a second vocabulary for the same idea.
   */
  const renderWantGroupQty = (wantEntry: WantContext) => {
    const spent = getWantEntryAddedQuantity(targetSide, wantEntry.id);
    const remaining = wantEntry.quantity - spent;
    return (
      <span className="offer-want-group-qty">
        {' '}
        · they want {wantEntry.quantity}
        {spent > 0 && (
          <span
            className={
              remaining <= 0 ? 'offer-want-group-filled' : 'offer-want-group-remaining'
            }
          >
            {remaining <= 0 ? ' · filled' : ` · ${remaining} left`}
          </span>
        )}
      </span>
    );
  };

  // The title is what teaches the counterintuitive pairing: it states
  // ownership in words, not whose list it is (decision 15).
  const title = side === 'send' ? 'Cards they want that you own' : 'Cards they own that you want';
  const listNoun = side === 'send' ? 'want list' : 'have list';
  const subtitle = `${counterpartyUsername}'s ${listNoun}${
    matchCount != null
      ? ` · ${matchCount.toLocaleString()} ${matchCount === 1 ? 'match' : 'matches'}`
      : ''
  }`;
  const fullListLabel = `Full ${listNoun}${
    fullListTotal != null ? ` (${fullListTotal.toLocaleString()})` : ''
  }`;

  const showFullToggle = side === 'send' || fullHaveListPublic;

  return (
    <section className="offer-source-panel" aria-label={title} aria-busy={isFetching}>
      <header className="offer-source-header">
        <h3 className="offer-source-title">{title}</h3>
        <div className="offer-source-subtitle">{subtitle}</div>
        <div className="offer-source-modes">
          <button
            type="button"
            className={`offer-mode-btn${mode === 'intersection' ? ' active' : ''}`}
            onClick={() => setMode('intersection')}
          >
            Matches
          </button>
          {showFullToggle ? (
            <button
              type="button"
              className={`offer-mode-btn${mode === 'full' ? ' active' : ''}`}
              onClick={() => setMode('full')}
            >
              {fullListLabel}
            </button>
          ) : (
            <span className="offer-mode-private">
              {counterpartyUsername} has not made their full collection public. Showing matched
              cards only.
            </span>
          )}
        </div>
        <div className="offer-source-count">
          {mode === 'intersection' ? 'Matches' : 'Full list'}
          {hasData ? ` (${totalCount.toLocaleString()})` : ''}
        </div>
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search cards…"
          ariaLabel={`Search ${title}`}
        />
      </header>

      <div className="offer-source-body">
        {isLoading ? (
          <div className="offer-source-status">Loading…</div>
        ) : isPrivateHaveList ? (
          <div className="offer-source-status">
            <p>{counterpartyUsername} has not made their full collection public.</p>
            <button
              type="button"
              className="offer-mode-btn"
              onClick={() => setMode('intersection')}
            >
              Back to matched cards
            </button>
          </div>
        ) : isProposerOffersDisabled ? (
          // Deliberately quiet: the page's enable-in-place dialog is the remedy
          // and is already on screen. Repeating the rejection behind it would
          // read as a dead end the dialog is actively offering to undo.
          <div className="offer-source-status">Trade offers are turned off for your account.</div>
        ) : parsedError ? (
          <div className="offer-source-status">{parsedError.message}</div>
        ) : isFullWantList ? (
          !wantListQuery.data || !matchIndex.isReady ? (
            <div className="offer-source-status">Loading…</div>
          ) : wantListQuery.data.wantList.length === 0 ? (
            <div className="offer-source-status">
              {effectiveSearch
                ? 'No cards found.'
                : `${counterpartyUsername}'s want list is empty.`}
            </div>
          ) : (
            wantListQuery.data.wantList.map((entry) => {
              const ownedGroup = matchIndex.index.get(entry.id);
              const matches = ownedGroup?.matches;
              if (matches && matches.length > 0) {
                // Owned entry: the exact Matches-mode group markup and add
                // path (decision 2), plus the owned marking (decision 4).
                return (
                  <div className="offer-want-group" key={entry.id}>
                    <div className="offer-want-group-header">
                      {entry.cardName || entry.card?.name}
                      {ownedGroup && renderWantGroupQty(ownedGroup.wantEntry)}
                    </div>
                    {matches.map((row) => (
                      <OfferCardRow
                        key={row.userMTGCardHaveId}
                        card={row.card}
                        foil={row.foil}
                        condition={row.condition}
                        pricePoints={row.quotedPricePoints}
                        quantityBadge={row.ownedQuantity}
                        owned
                        action={renderAddAction(row, ownedGroup?.wantEntry)}
                      />
                    ))}
                  </div>
                );
              }
              return (
                <WantListInfoRow
                  key={entry.id}
                  item={entry}
                  calculatedPrice={priceMap[entry.id]}
                  onAdd={setAddingWant}
                  addDisabledReason={atCap ? 'This side is at the 100-card limit' : undefined}
                  // The BUDGET, not mere presence: a want of 2 stays addable
                  // after the first copy. Same rule as the catalogued rows, so
                  // the two paths cannot disagree about when a want is filled.
                  added={
                    getWantEntryAddedQuantity(targetSide, entry.id) >= entry.quantity
                  }
                />
              );
            })
          )
        ) : side === 'send' && sendQuery.data ? (
          sendQuery.data.candidates.length === 0 ? (
            <div className="offer-source-status">
              {mode === 'intersection'
                ? effectiveSearch
                  ? 'No matching cards found.'
                  : `You don't own any cards on ${counterpartyUsername}'s want list yet.`
                : 'No cards found.'}
            </div>
          ) : (
            sendQuery.data.candidates.map((group) => (
              <div className="offer-want-group" key={group.wantEntry.id}>
                <div className="offer-want-group-header">
                  {group.wantEntry.cardName}
                  {renderWantGroupQty(group.wantEntry)}
                </div>
                {group.matches.length === 0 ? (
                  <div className="offer-want-unowned">
                    You don&apos;t own this card, so it can&apos;t be added, but they want it.
                  </div>
                ) : (
                  group.matches.map((row) => (
                    <OfferCardRow
                      key={row.userMTGCardHaveId}
                      card={row.card}
                      foil={row.foil}
                      condition={row.condition}
                      pricePoints={row.quotedPricePoints}
                      quantityBadge={row.ownedQuantity}
                      action={renderAddAction(row, group.wantEntry)}
                    />
                  ))
                )}
              </div>
            ))
          )
        ) : side === 'receive' && receiveQuery.data ? (
          receiveQuery.data.candidates.length === 0 ? (
            <div className="offer-source-status">
              {mode === 'intersection'
                ? effectiveSearch
                  ? 'No matching cards found.'
                  : `None of your want list cards are on ${counterpartyUsername}'s have list yet.`
                : 'No cards found.'}
            </div>
          ) : (
            receiveQuery.data.candidates.map((row) => (
              <OfferCardRow
                key={row.userMTGCardHaveId}
                card={row.card}
                foil={row.foil}
                condition={row.condition}
                pricePoints={row.quotedPricePoints}
                quantityBadge={row.ownedQuantity}
                action={renderAddAction(row)}
              />
            ))
          )
        ) : null}
      </div>

      {hasData && totalPages > 1 && !isPrivateHaveList && (
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      <AddUncataloguedCardModal
        isOpen={addingWant != null}
        want={addingWant}
        onClose={() => setAddingWant(null)}
        onConfirm={(selection) => handleUncataloguedAdd(addingWant as CollectionList, selection)}
      />
    </section>
  );
};

export default SourcePanel;
